import React, { useRef, useEffect } from "react";
import { Plus, Loader2 } from "lucide-react";
import { Button } from "../../../../components/UI/Button";
import { DateRangePicker } from "../../../../components/UI/DateRangePicker";
import type { OrderSearchSuggestion } from "../../../../../domain/types/orders";
import { OrderSearchInput } from "./OrderSearchInput";
import Table from "../../../../components/UI/Table";
import type { Order, OrderStatus } from "../types";
import { Status } from "../types";
import { orderDisplayTitle } from "../orderDisplayTitle";
import { StatusLabel } from "./StatusLabel";

interface OrderListProps {
  startDate: string;
  endDate: string;
  onDateRangeChange: (startDate: string, endDate: string) => void;
  dateRangeInvalid: boolean;
  fetchSearchSuggestions: (keyword: string) => Promise<OrderSearchSuggestion[]>;
  onShowStatistics: () => void;
  refreshError: string;
  orders: Order[];
  searchKeyword: string;
  selectedOrderStatus: OrderStatus | "all";
  isResetButtonEnabled: boolean;
  onSearchKeywordChange: (keyword: string) => void;
  onStatusFilterChange: (status: OrderStatus | "all") => void;
  onResetFilters: () => void;
  onCreateOrder: () => void;
  onCreateCustomOrder: () => void;
  onRowClick: (order: Order) => void;
  onLoadMore: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
}

const orderStatusToStatus = (status: OrderStatus): Status | null => {
  switch (status) {
    case "pending_payment":
      return Status.PendingPayment;
    case "accepted":
      return Status.Accepted;
    case "resubmission":
      return Status.Resubmission;
    case "production":
      return Status.Production;
    case "production_complete":
      return Status.ProductionComplete;
    case "shipping":
      return Status.Shipping;
    case "delivered":
      return Status.Delivered;
    case "cancelled":
      return Status.Cancelled;
    case "refunded":
      return Status.Refunded;
    default:
      return null;
  }
};

export const OrderList: React.FC<OrderListProps> = ({
  startDate, endDate, onDateRangeChange, dateRangeInvalid,
  fetchSearchSuggestions, onShowStatistics, refreshError,
  orders,
  searchKeyword,
  selectedOrderStatus,
  isResetButtonEnabled,
  onSearchKeywordChange,
  onStatusFilterChange,
  onResetFilters,
  onCreateOrder,
  onCreateCustomOrder,
  onRowClick,
  onLoadMore,
  hasMore,
  isLoadingMore,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

  // Intersection Observer를 사용한 무한 스크롤
  useEffect(() => {
    const loadMoreTrigger = loadMoreTriggerRef.current;
    if (!loadMoreTrigger) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !isLoadingMore) {
          onLoadMore();
        }
      },
      {
        root: null,
        rootMargin: "100px",
        threshold: 0.1,
      }
    );

    observer.observe(loadMoreTrigger);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isLoadingMore, onLoadMore]);

  return (
    <div ref={scrollContainerRef} className="p-5">
      <div className="mb-[28px] flex flex-col md:flex-row md:justify-between gap-[20px] items-center justify-between">
        <div className="w-full flex flex-col items-start gap-[0.3rem]">
          <h2 className="text-[1.375rem] font-semibold">전체 주문 관리</h2>
          <span className="text-[1rem] font-normal text-left">
            모든 주문을 조회하고 관리하세요.
          </span>
        </div>
        <div className="flex gap-2 md:justify-end w-full">
          <Button onClick={onCreateOrder} className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            일반 주문
          </Button>
          <Button
            onClick={onCreateCustomOrder}
            className="flex items-center gap-2"
            variant="outline"
          >
            <Plus className="w-4 h-4" />
            커스텀 주문
          </Button>
        </div>
      </div>

      {refreshError && <p role="alert" className="mb-3 text-[13px] text-[#4285F4]">자동 갱신 실패: {refreshError} 잠시 후 자동으로 다시 시도합니다.</p>}
      <div>
        <div className="mb-2 flex flex-col md:flex-row md:items-center gap-2">
          <OrderSearchInput
            value={searchKeyword}
            onChange={onSearchKeywordChange}
            fetchSuggestions={fetchSearchSuggestions}
            className="flex-1 min-w-0"
          />

          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={onDateRangeChange}
            placeholder="주문일 전체 기간"
            className="flex-shrink-0 w-full md:w-[15rem]"
          />

          <div className="relative flex-shrink-0 w-full md:w-[9.5rem] h-10">
            <img
              className="absolute left-3 top-1/2 -translate-y-1/2 w-[1rem] pointer-events-none"
              alt=""
              src="/assets/icons/Filter 3.svg"
            />
            <select
              aria-label="주문 상태"
              className="w-full h-full bg-white pl-9 pr-8 text-sm rounded-[8px] border border-[#e0e0e0] hover:border-[#4285F4]
              appearance-none focus:outline-none focus:border-[#4285F4] text-[#222222] font-medium cursor-pointer"
              value={selectedOrderStatus}
              onChange={(e) =>
                onStatusFilterChange(e.target.value as OrderStatus | "all")
              }
            >
              <option value="all">전체 상태</option>
              <option value="pending_payment">결제전</option>
              <option value="accepted">접수</option>
              <option value="resubmission">재접수</option>
              <option value="production">제작</option>
              <option value="production_complete">제작 완료</option>
              <option value="shipping">출고/배송</option>
              <option value="delivered">배송 완료</option>
              <option value="cancelled">취소</option>
              <option value="refunded">환불</option>
            </select>
            <img
              className="absolute right-3 top-1/2 -translate-y-1/2 w-[0.6837rem] pointer-events-none"
              alt=""
              src="/assets/icons/Arrow - Down 2.svg"
            />
          </div>

          <div className="flex gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={onResetFilters}
              disabled={!isResetButtonEnabled}
              className="flex-1 md:flex-none h-10 px-4 text-sm rounded-[8px] border border-[#e0e0e0] bg-white text-[#555555] hover:bg-[#f5f5f5]
              disabled:bg-[#f5f5f5] disabled:text-[#BBBBBB] disabled:hover:bg-[#f5f5f5] disabled:cursor-default"
            >
              초기화
            </button>
            <button
              type="button"
              onClick={onShowStatistics}
              disabled={dateRangeInvalid}
              className="flex-1 md:flex-none h-10 px-4 flex items-center justify-center gap-1.5 text-sm font-medium rounded-[8px] bg-[#4285F4] text-white hover:bg-[#3367D6] disabled:opacity-50"
            >
              <img className="w-4 brightness-0 invert" alt="" src="/assets/icons/Chart.svg" />
              통계보기
            </button>
          </div>
        </div>
        <p className="mb-4 text-right text-[12px] text-[#888888]">
          5초마다 자동 갱신 · 주문일은 접수일 기준 (기존 주문은 생성일)
        </p>

        <div className="text-[0.75rem]">
          <Table
            tabledata={{
              head: [
                <>주문번호</>,
                <>주문자</>,
                <>상품명</>,
                <>작업물 제목 / 주문 구분</>,
                <>수량</>,
                <>금액</>,
                <>상태</>,
                <>주문일</>,
              ],
              body: orders.map((item) => {
                const status = orderStatusToStatus(item.status);
                return [
                  <>{item.orderNumber}</>,
                  <>{item.customerName}</>,
                  <>{item.productName}</>,
                  <span className="break-words" title={orderDisplayTitle(item)}>{orderDisplayTitle(item)}</span>,
                  <>{item.quantity.toLocaleString()}</>,
                  <>{item.totalAmount > 0 ? `₩${item.totalAmount.toLocaleString()}` : '-'}</>,
                  <>
                    <StatusLabel status={status} />
                  </>,
                  <>{item.orderDate}{item.isLegacyDate && <span className="block text-gray-500">기존 생성일</span>}</>,
                ];
              }),
            }}
            onRowClick={(rowIndex) => {
              const order = orders[rowIndex];
              onRowClick(order);
            }}
          />

          {/* 무한 스크롤 트리거 및 로딩 UI */}
          <div ref={loadMoreTriggerRef} className="py-4">
            {isLoadingMore && (
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">주문을 불러오는 중...</span>
              </div>
            )}
            {!hasMore && orders.length > 0 && (
              <div className="text-center text-gray-400 text-sm py-2">
                모든 주문을 불러왔습니다.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

